// MF-566 — Card 'student-profile': the header of a student dashboard — photo, name, a line of facts
// (year, student ID, form group, house) and, when the tile asks for it and the viewer is staff, the
// medical flags as chips. The server decides what is sent: a missing field is simply not drawn.
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Typography } from '@mui/material'
import globalConfig from '../../../../../config/globalConfig'
import { SUMMARY_PALETTE } from '../../../Dashboard/StudentSummary/summary.palette'

const CHIP = { bgcolor: '#fdf3cf', color: '#7a5b00' }

// A tall photo that fills the height of the tile (portrait, like a school ID photo). The file lives
// in the tenant file store, same convention as the app's avatars; no photo (or a missing file) shows
// the initials on the same tall block.
const Photo = ({ photo, initials }) => {
  const tenantId = useSelector((state) => state?.tenant?.tenant?.tenantId || null)
  const url = photo && tenantId ? `${globalConfig.storage}/tenants/${tenantId}/photos/${photo}` : null
  const [ok, setOk] = useState(false)
  useEffect(() => {
    setOk(false)
    if (!url) return undefined
    const img = new Image()
    img.onload = () => setOk(true)
    img.src = url
    return () => {
      img.onload = null
    }
  }, [url])
  return (
    <Box
      sx={{
        height: '100%',
        aspectRatio: '3 / 4',
        maxWidth: '45%',
        flexShrink: 0,
        borderRadius: '10px',
        overflow: 'hidden',
        bgcolor: SUMMARY_PALETTE.grid,
        color: SUMMARY_PALETTE.muted,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 40,
        fontWeight: 700,
        backgroundImage: ok ? `url("${url}")` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center top'
      }}
    >
      {ok ? null : initials}
    </Box>
  )
}

const yearText = (y) => (y ? (/year/i.test(String(y)) ? y : `Year ${y}`) : null)
const splitFlags = (v) =>
  String(v || '')
    .split(/[,;|]/)
    .map((s) => s.trim())
    .filter(Boolean)

const StudentProfileCard = ({ data }) => {
  const r = data?.rows?.[0]
  if (!r) return null
  const facts = [
    yearText(r.year_level),
    r.code ? `Student ID ${r.code}` : null,
    r.form_group ? `Roll Class ${r.form_group}` : null,
    r.house ? `House ${r.house}` : null,
    r.date_of_birth ? `DOB ${r.date_of_birth}` : null
  ].filter(Boolean)
  // Medical flags and origin share one row of chips; both arrive only when staff switched them on.
  const flags = [...splitFlags(r.medical), ...(r.aboriginal_status ? [r.aboriginal_status] : [])]
  const initials = String(r.name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('')

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2.5,
        height: '100%',
        minWidth: 0,
        overflow: 'hidden'
      }}
    >
      <Photo photo={r.photo} initials={initials} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 28,
            fontWeight: 600,
            letterSpacing: '.02em',
            color: SUMMARY_PALETTE.ink,
            textTransform: 'uppercase'
          }}
        >
          {r.name}
        </Typography>
        <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted, mt: 0.5 }}>
          {facts.join('  •  ')}
        </Typography>
        {flags.length ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
            {flags.map((f) => (
              <Box key={f} sx={{ ...CHIP, px: 1.5, py: 0.25, borderRadius: '12px', fontSize: 12 }}>
                {f}
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}

export default StudentProfileCard
